/**
 * Lead → Quote → Price — the CLEAN redesign logic (2026-06-15).
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  WHAT THIS IS (built fresh from the owner-approved mockups, NOT lifted)
 * ─────────────────────────────────────────────────────────────────────────
 *  The prior quote attempt re-used cramped legacy screens and was rejected.
 *  This is the clean rebuild driven by:
 *    · QUOTATIONS_AND_PRICING_PLAIN_SPEC_2026-06-15
 *    · MOCKUP_1 (Lead numbered path) · MOCKUP_2 (Set price — the heart)
 *    · MOCKUP_5 (Send — WhatsApp-first) · MOCKUP_6 (Quotes record)
 *
 *  THE ONE IDEA: "Whatever price you agree IS what the client pays." The
 *  agreed price set here flows onto the lead and, on Convert, seeds the
 *  money gate via the EXISTING agreed-price plumbing
 *  (engagementTemplate.seedGateAmounts) — so the front-door price and the
 *  post-conversion money gates can never disagree (the Tarveen $40 fix at
 *  the root).
 *
 *  THE 6 MUST-HAVES (spec "Can't-skip-what's-needed"): a quote can't go out
 *  and a lead can't convert with any of these blank —
 *    1 Client name · 2 WhatsApp number · 3 Email · 4 Service ·
 *    5 Agreed price · 6 GST invoice needed? (yes/no)
 *  A "No charge" engagement waives #5 (price) but keeps the rest.
 *
 *  ONE QUOTE PATH: the price/quote is set from the lead (Set-price screen).
 *  The Quotes list is the RECORD (view + edit), never a second create path.
 *  No competing inline tier-quote.
 *
 *  PURE + dependency-light (no Firebase, no React) so the Node test runner
 *  exercises every branch directly. The agreed price rides on the `Lead`
 *  model's optional fields (round-trips inside the leadStore JSON snapshot —
 *  no Firestore rules change).
 * ─────────────────────────────────────────────────────────────────────────
 */
import type { Currency, FirmId } from './firms';
import { firmFor } from './firms';
// `tierOptions` is a VALUE import; no cycle — leads.ts never imports this module.
import { tierOptions, type Lead } from './leads';
import { REFRESHED_CATALOGUE, type ServiceCatalogEntry } from './rateCard';
import type { CoreTier, Tier } from './engagementTemplate';

/* ════════════════════ Standard price (rate-card lookup) ════════════════════ */

/** A service's standard price in both currencies — from the rate card. */
export interface StandardPrice {
  tier: string;
  name: string;
  /** Standard INR rate (₹0 for a formula/custom tier). */
  rINR: number;
  /** Standard USD rate. */
  rUSD: number;
  /** Formula-priced (L3B) — the standard rate is not a flat figure. */
  isFormula: boolean;
}

/**
 * PURE — the standard (rate-card) price for a tier, used to pre-fill the
 * "Agreed price" field. Reads the editable catalogue passed in (so a live
 * `services/` read overrides), falling back to the locked REFRESHED_CATALOGUE
 * default. Returns null for an unknown tier.
 */
export function standardPriceFor(
  tier: string,
  catalogue: ServiceCatalogEntry[] = REFRESHED_CATALOGUE,
): StandardPrice | null {
  const svc =
    catalogue.find((s) => s.svcId === tier) ??
    catalogue.find((s) => s.tier === tier);
  if (!svc) return null;
  return {
    tier: svc.tier,
    name: svc.name,
    rINR: svc.rINR,
    rUSD: svc.rUSD,
    isFormula: svc.isFormula === true,
  };
}

/** PURE — the standard amount in a given currency (₹ or $). */
export function standardAmount(std: StandardPrice, currency: Currency): number {
  return currency === 'USD' ? std.rUSD : std.rINR;
}

/* ════════════════════ Canonical service names (owner fix 2026-07-03) ════════════════════
 *
 *  ONE client-facing name per service, everywhere. The owner's live
 *  walkthrough caught the lead picker saying "L1A · The Pre-Possession Path ·
 *  Vichara" while the quote line + rate card say "The Selection Path · Disha".
 *
 *  THE CANON: the rate card's Set-4 "Modern + Sanskrit Seal" names
 *  (REFRESHED_CATALOGUE — reconciled VERBATIM from the locked naming memory
 *  `project_tier_naming_set_locked` + the LIVE report templates: functions
 *  l1aTemplate.js prints "The Selection Path · Disha"; the delivered VP667
 *  PDF is titled the same). `intakeCatalog.ts` already upgraded the spine's
 *  older names to this canon; `engagementTemplate.displayName` still carries
 *  the pre-Set-4 spec v1.1.0 names (that file's workflow config is
 *  edit-frozen), so DISPLAY sites read the canon through these helpers
 *  instead — picker = quote lines = rate card, one name.
 */

/** PURE — the canonical Set-4 name for a tier off the rate card (null when
 *  the catalogue doesn't carry the tier — e.g. ancillary codes). */
export function canonicalServiceNameFor(
  tier: string,
  catalogue: ServiceCatalogEntry[] = REFRESHED_CATALOGUE,
): string | null {
  const svc =
    catalogue.find((s) => s.svcId === tier) ??
    catalogue.find((s) => s.tier === tier);
  return svc?.name ?? null;
}

/** PURE — `tierOptions()` with every displayName upgraded to the Set-4 canon
 *  (rate-card name wins; the template displayName is only the fallback for a
 *  tier the catalogue doesn't carry). EVERY service picker / tier label
 *  renders through this — never bare `tierOptions()` — so no surface can
 *  drift back to the stale spine names. */
export function canonicalTierOptions(): Array<{ tier: CoreTier; displayName: string }> {
  return tierOptions().map((o) => ({
    tier: o.tier,
    displayName: canonicalServiceNameFor(o.tier) ?? o.displayName,
  }));
}

/* ════════════════════ The agreed price on a lead ════════════════════ */

/** The agreed-price snapshot of a lead (what the price/send screens read). */
export interface LeadPrice {
  currency: Currency;
  /** Agreed amount in `currency` units, or null when no price is set. */
  value: number | null;
  gst: boolean;
  firmId: FirmId;
  noCharge: boolean;
  noChargeReason: string;
}

/** PURE — read the agreed-price snapshot off a lead (defensive defaults). */
export function leadPriceOf(lead: Lead): LeadPrice {
  const currency: Currency = lead.agreedCurrency === 'USD' ? 'USD' : 'INR';
  const gst = lead.gstRequired === true;
  return {
    currency,
    value:
      typeof lead.agreedValue === 'number' && lead.agreedValue > 0
        ? lead.agreedValue
        : null,
    gst,
    firmId: (lead.firmId as FirmId) ?? firmFor({ currency, gst }),
    noCharge: lead.noCharge === true,
    noChargeReason: lead.noChargeReason ?? '',
  };
}

/** The Set-price form input (MOCKUP_2). */
export interface SetPriceInput {
  /** The tier the lead wants (service). */
  tier: Tier | string | null;
  currency: Currency;
  /** Agreed amount in `currency` units. Ignored when noCharge. */
  value: number | null;
  /** GST invoice needed? (drives the firm). */
  gst: boolean;
  /** Optional explicit firm override (else auto-routed from currency+gst). */
  firmId?: FirmId | null;
  noCharge?: boolean;
  noChargeReason?: string;
}

/**
 * PURE — apply a Set-price decision to a lead, returning a NEW lead (never
 * mutates). Writes the agreed-price fields + the assigned tier; auto-routes
 * the firm from {currency, gst} unless an explicit (valid) firmId is given.
 * A "No charge" engagement clears the value and records the reason. Does NOT
 * change the lead's pipeline state — the screen advances state separately
 * (quoting / convert) so this stays a single-responsibility price write.
 */
export function applyLeadPrice(
  lead: Lead,
  input: SetPriceInput,
  now: string,
): Lead {
  const currency: Currency = input.currency === 'USD' ? 'USD' : 'INR';
  const noCharge = input.noCharge === true;
  const gst = noCharge ? false : input.gst === true;
  const firmId: FirmId =
    input.firmId === 'firm1' || input.firmId === 'firm2'
      ? input.firmId
      : firmFor({ currency, gst });
  const value =
    noCharge || input.value == null || !(input.value > 0)
      ? null
      : Math.round(Number(input.value));
  return {
    ...lead,
    assignedTier: (input.tier as Tier) ?? lead.assignedTier,
    desiredTier: lead.desiredTier ?? (input.tier as Tier) ?? null,
    agreedCurrency: currency,
    agreedValue: noCharge ? 0 : value,
    gstRequired: gst,
    firmId,
    noCharge,
    noChargeReason: noCharge ? (input.noChargeReason ?? '').trim().slice(0, 200) : null,
    updatedAt: now,
  };
}

/* ════════════════════ The 6 must-haves (can't-skip validation) ════════════════════ */

/** One required item + whether the lead currently satisfies it. */
export interface ChecklistItem {
  key:
    | 'name'
    | 'whatsapp'
    | 'email'
    | 'service'
    | 'price'
    | 'gst';
  label: string;
  /** true → filled (green tick); false → "needs this" (amber). */
  ok: boolean;
  /** Short hint shown when not ok. */
  hint: string;
}

/** A lightweight email-shape check (good enough for triage gating). */
function looksEmail(s: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.trim());
}

/** A lightweight phone-shape check — at least 7 digits. */
function looksPhone(s: string): boolean {
  return (s.match(/\d/g)?.length ?? 0) >= 7;
}

/**
 * PURE — the 6 must-haves for a lead, in order (MOCKUP_1's numbered path).
 * A "No charge" engagement still requires name/whatsapp/email/service/GST —
 * only the PRICE item is auto-satisfied (the reason is the price). The GST
 * item is satisfied once a yes/no answer exists on the lead (gstRequired is a
 * real boolean — distinguished from "never answered" by the price snapshot
 * carrying the firm). We treat GST as answered when a price has been set OR
 * gstRequired is explicitly set.
 */
export function leadChecklist(lead: Lead): ChecklistItem[] {
  const price = leadPriceOf(lead);
  const gstAnswered =
    typeof lead.gstRequired === 'boolean' || lead.noCharge === true;
  return [
    {
      key: 'name',
      label: 'Client name',
      ok: !!lead.name && lead.name.trim().length > 0,
      hint: 'Add the client’s name.',
    },
    {
      key: 'whatsapp',
      label: 'WhatsApp number',
      ok: looksPhone(lead.phone ?? ''),
      hint: 'Add the WhatsApp / phone number.',
    },
    {
      key: 'email',
      label: 'Email address',
      ok: looksEmail(lead.email ?? ''),
      hint: 'Add a valid email address.',
    },
    {
      key: 'service',
      label: 'Service they want',
      ok: !!lead.assignedTier,
      hint: 'Pick the service / tier.',
    },
    {
      key: 'price',
      label: 'Agreed price',
      ok: price.noCharge || price.value != null,
      hint: 'Set the agreed price (or mark it No charge).',
    },
    {
      key: 'gst',
      label: 'GST invoice needed?',
      ok: gstAnswered,
      hint: 'Answer GST invoice needed — yes or no.',
    },
  ];
}

/** PURE — the keys of the must-haves still blank (empty → ready). */
export function missingMustHaves(lead: Lead): ChecklistItem['key'][] {
  return leadChecklist(lead)
    .filter((c) => !c.ok)
    .map((c) => c.key);
}

/** PURE — are all 6 must-haves satisfied (ready to quote / convert)? */
export function isLeadComplete(lead: Lead): boolean {
  return missingMustHaves(lead).length === 0;
}

/**
 * PURE — a plain-language gate message for the footer when something's blank
 * (MOCKUP_1 Frame 2 "Add the email and the agreed price to continue."), or
 * null when the lead is ready. Lists the missing item labels, joined "and".
 */
export function completionMessage(lead: Lead): string | null {
  const miss = leadChecklist(lead).filter((c) => !c.ok);
  if (miss.length === 0) return null;
  const labels = miss.map((c) => c.label.toLowerCase());
  const joined =
    labels.length === 1
      ? labels[0]
      : labels.slice(0, -1).join(', ') + ' and ' + labels[labels.length - 1];
  return `Add the ${joined} to continue.`;
}

/* ════════════════════ THE Convert gate (2026-07-03 · streamline Phase 1d) ════════════════════
 *
 *  The ONE lib predicate for "may this lead be converted through the MANUAL
 *  door right now". It gates on the AGREED PRICE + the 6 must-haves (Option A,
 *  owner-locked 2026-06-17) — NOT on the lead sitting in the `quoted` state
 *  (that leftover coupling was queued for removal in AVBP_LEADQUOTE_STATE.md;
 *  `convertLeadDirect` already synthesises the quote snapshot itself).
 *
 *  It ALSO closes the double-conversion door: a lead with an OPEN SENT quote
 *  must be resolved through the quote (Accept — which itself converts — or
 *  void it via supersede/expire), never converted around it, or the accepted
 *  total and the manually-set price would race for the money gate. The caller
 *  passes the lead's open sent quote id (from `listenQuotesForLead` /
 *  `findOpenSentQuoteIdForLead`); `null`/`undefined` means none known.
 */

export interface ConvertGateResult {
  ok: boolean;
  /** Plain-language reason when blocked; null when ok. */
  reason: string | null;
}

/** PURE — evaluate the manual-convert gate for a lead. */
export function convertGate(
  lead: Lead,
  openSentQuoteId?: string | null,
): ConvertGateResult {
  if (lead.state === 'converted' || lead.convertedProjectId) {
    return {
      ok: false,
      reason: `This lead is already converted${lead.convertedProjectId ? ` (project ${lead.convertedProjectId})` : ''}.`,
    };
  }
  if (openSentQuoteId) {
    return {
      ok: false,
      reason:
        `This lead has an open sent quote (${openSentQuoteId}). ` +
        `Accept it (accepting converts the lead and seeds the agreed price) ` +
        `or void it (supersede/expire) before converting manually.`,
    };
  }
  const msg = completionMessage(lead);
  if (msg) return { ok: false, reason: msg };
  return { ok: true, reason: null };
}

/**
 * PURE — the first OPEN (status 'sent') quote id among a lead's quotes,
 * excluding `excludeQuoteId` (the accept-driven conversion passes its OWN
 * quote id so the quote being accepted never blocks itself). Null when none.
 * Works on raw doc-ish rows so both the store query and tests can use it.
 */
export function firstOpenSentQuoteId(
  rows: Array<{ id: string; status: string }>,
  excludeQuoteId?: string | null,
): string | null {
  for (const r of rows) {
    if (r.status === 'sent' && r.id !== excludeQuoteId) return r.id;
  }
  return null;
}
